/**
 * Splister REST API Types
 * Generated from OpenAPI spec v1.0.0
 */

// ==================== AUTH ====================

export interface LoginRequest {
  code: string;
  code_verifier: string;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: UserProfile;
}

export interface TokenRefreshRequest {
  refresh_token: string;
}

export interface TokenRefreshResponse {
  accessToken: string;
  expiresIn: number;
}

export interface AuthStatus {
  authenticated: boolean;
  user: {
    id: string;
    displayName: string;
  };
}

// ==================== USER ====================

export interface UserProfile {
  id: string;
  displayName: string;
  email?: string;
  country?: string;
  product?: 'free' | 'premium';
  images?: Image[];
}

export interface UserSettingsUpdate {
  defaultCatalogDir?: string;
  autoCacheFeatures?: boolean;
}

// ==================== PLAYLIST ====================

export interface PlaylistListResponse {
  playlists: PlaylistSummary[];
  total: number;
  limit: number;
  offset: number;
}

export interface PlaylistSummary {
  id: string;
  name: string;
  description?: string | null;
  tracksCount: number;
  owner: string;
  public: boolean;
  collaborative: boolean;
  snapshotId: string;
  uri: string;
  images?: Image[];
}

export interface PlaylistTracks {
  total: number;
  items: PlaylistTrack[];
}

export interface PlaylistDetail {
  id: string;
  name: string;
  description?: string | null;
  public: boolean;
  collaborative: boolean;
  owner: User;
  snapshotId: string;
  uri: string;
  images?: Image[];
  tracks: PlaylistTracks;
}

export interface PlaylistTrack {
  track: Track;
  addedAt?: string;
  addedBy?: User;
}

export interface PlaylistCreate {
  name: string;
  description?: string;
  public?: boolean;
}

export interface PlaylistCreated {
  id: string;
  name: string;
  uri: string;
}

export interface PlaylistUpdate {
  name?: string;
  description?: string;
  public?: boolean;
}

export interface AddTracksRequest {
  uris: string[];
  position?: number;
}

export interface RemoveTracksRequest {
  uris: string[];
}

export interface SnapshotResponse {
  snapshotId: string;
}

export type ReorderStrategyType =
  | 'full_dj_mode'
  | 'scarecrow_groove'
  | 'camelot_chain'
  | 'energy_journey'
  | 'multi_sort'
  | 'greedy_tsp';

export interface ReorderStrategyInfo {
  id: ReorderStrategyType;
  name: string;
  description: string;
  icon?: string;
}

export const REORDER_STRATEGIES: ReorderStrategyInfo[] = [
  {
    id: 'full_dj_mode',
    name: 'Full DJ Mode',
    description: 'Camelot + BPM + Energy optimization',
  },
  {
    id: 'scarecrow_groove',
    name: 'Scarecrow Groove',
    description: 'Narrative Americana with Southern Gothic mood',
  },
  {
    id: 'camelot_chain',
    name: 'Camelot Chain',
    description: 'Harmonic key progression',
  },
  {
    id: 'energy_journey',
    name: 'Energy Journey',
    description: 'Energy curve following',
  },
  {
    id: 'multi_sort',
    name: 'Multi Sort',
    description: 'Multi-attribute sorting',
  },
  {
    id: 'greedy_tsp',
    name: 'Smooth TSP',
    description: 'Traveling salesman optimization',
  },
];

export interface PlaylistReorderRequest {
  strategy?: ReorderStrategyType;
  applyToSpotify?: boolean;
  startTrackId?: string | null;
}

export interface PlaylistReorderResponse {
  track_uris: string[];
  original_count: number;
  reordered_count: number;
  missing_features_count: number;
  quality_score: number;
  strategy_used: string;
  applied: boolean;
  snapshotId?: string | null;
}

export interface PlaylistImportRequest {
  text: string;
  mode: 'add' | 'replace';
  delimiter?: string | null;
}

export interface ImportMatchSummary {
  line: string;
  status: 'found' | 'ambiguous' | 'not_found';
  track_name?: string | null;
  track_uri?: string | null;
}

export interface PlaylistImportResponse {
  total_lines: number;
  found: number;
  ambiguous: number;
  not_found: number;
  added_tracks: number;
  matches: ImportMatchSummary[];
  snapshotId: string;
}

export interface WorkbenchPromptResponse {
  prompt: string;
  detectedType: 'category' | 'playlist' | 'theme';
  context: {
    playlist_name: string;
    track_count: number;
    avg_energy?: number | null;
    avg_valence?: number | null;
    avg_tempo?: number | null;
    avg_danceability?: number | null;
  };
}

// ==================== TRACK ====================

export interface Track {
  id: string;
  name: string;
  uri: string;
  durationMs: number;
  artists: Artist[];
  album: Album;
  explicit: boolean;
  popularity?: number | null;
  isrc?: string | null;
  isLiked?: boolean; // Populated by useAreLiked() or true for Liked Songs
}

export type TrackWithInstanceId = Track & {
  instanceId: string;
};

export interface Artist {
  id: string;
  name: string;
  uri: string;
}

export interface Album {
  id: string;
  name: string;
  releaseDate: string;
  images?: Image[];
  artists: Artist[];
  uri: string;
}

export interface Image {
  url: string;
  height?: number | null;
  width?: number | null;
}

export interface User {
  id: string;
  displayName?: string | null;
  uri: string;
}

export interface TrackSearchParams {
  q: string;
  year_start?: number;
  year_end?: number;
  genre?: string;
  limit?: number;
}

export interface TrackSearchResponse {
  tracks: Track[];
  total: number;
  limit: number;
}

export interface AnalyzeTracksRequest {
  trackIds: string[];
}

export interface AnalyzeTracksResponse {
  tracks: TrackWithFeatures[];
}

export interface TrackWithFeatures {
  track: Track;
  features?: AudioFeatures | null;
}

// ==================== GENRES ====================

export interface GenreTag {
  name: string;
  weight: number;
  provider: string;
}

export interface TrackGenres {
  trackId: string;
  genres: GenreTag[];
  providers: string[];
  isUserEdited: boolean;
}

export interface BatchGenreRequest {
  trackIds: string[];
}

export interface BatchGenreResponse {
  tracks: TrackGenres[];
  totalRequested: number;
  totalReturned: number;
}

export interface LikedTracksParams {
  limit?: number;
  offset?: number;
}

export interface LikedTracksResponse {
  tracks: Track[];
  total: number;
  limit: number;
  offset: number;
}

// ==================== AUDIO FEATURES ====================

export interface AudioFeatures {
  id: string;
  energy: number;
  valence: number;
  danceability: number;
  tempo: number;
  loudness: number;
  acousticness: number;
  instrumentalness: number;
  speechiness: number;
  liveness: number;
  key: number;
  mode: 0 | 1;
  timeSignature?: number;
  durationMs?: number;
}

export interface AudioFeaturesWithMetadata extends AudioFeatures {
  cachedAt?: string;
}

export interface BatchAudioFeaturesResponse {
  audioFeatures: AudioFeatures[];
}

// ==================== CACHE ====================

export interface CacheStats {
  totalTracks: number;
  audioFeaturesCount: number;
  trackMetadataCount: number;
  cacheSizeMb: number;
  oldestEntry?: string | null;
  newestEntry?: string | null;
}

export interface VacuumResult {
  sizeBeforeMb: number;
  sizeAfterMb: number;
  savedMb: number;
  durationSeconds: number;
}

export interface CacheImportResult {
  importedAudioFeatures: number;
  importedMetadata: number;
  skipped: number;
}

// ==================== CATALOG ====================

export interface CatalogListResponse {
  catalogs: CatalogSummary[];
}

export interface CatalogSummary {
  name: string;
  trackCount: number;
  createdAt: string;
  sizeMb: number;
}

export interface CatalogDetail {
  name: string;
  trackCount: number;
  createdAt: string;
  tracks?: TrackWithFeatures[];
  stats?: CatalogStats;
}

export interface CatalogStats {
  energyAvg: number;
  energyMin: number;
  energyMax: number;
  tempoAvg: number;
  valenceAvg: number;
}

export interface CatalogParams {
  include_tracks?: boolean;
  include_stats?: boolean;
}

export interface ImportPlaylistRequest {
  playlistId: string;
  catalogName: string;
  skipFeatures?: boolean;
}

// ==================== CRATE ====================

export interface CrateListResponse {
  crates: CrateSummary[];
}

export interface CrateSummary {
  name: string;
  poolSize: number;
  rotationMode: 'SHUFFLE' | 'FIFO';
  searchDepthPct: number;
  deckRemaining: number;
}

export interface CrateDetail {
  name: string;
  poolSize: number;
  rotationMode: 'SHUFFLE' | 'FIFO';
  searchDepthPct: number;
  deckStatus?: DeckStatus;
}

export interface DeckStatus {
  total: number;
  used: number;
  remaining: number;
  usagePct: number;
}

export interface CratesCreated {
  createdCrates: string[];
}

export interface DeckFillRequest {
  reset?: boolean;
}

export interface DeckFillResult {
  deckSize: number;
  message: string;
}

export interface CrateExportRequest {
  playlistName?: string;
  playlistId?: string;
  deckOnly?: boolean;
  replace?: boolean;
  public?: boolean;
}

export interface CrateExportResult {
  playlistId: string;
  trackCount: number;
}

// ==================== SEQUENCE ====================

export interface SequenceLogsResponse {
  logs: SequenceLogSummary[];
}

export interface SequenceLogSummary {
  sequenceName: string;
  trackCount: number;
  timestamp: string;
  status: 'SUCCESS' | 'FAILED' | 'PARTIAL';
  logFile: string;
}

export interface SequencePreview {
  sequenceName: string;
  clockEvents: ClockEvent[];
  estimatedDurationMinutes: number;
  estimatedTracks: number;
}

export interface ClockEvent {
  time: string;
  event: 'track' | 'stopset';
  crate?: string | null;
  count: number;
}

export interface SequenceAuditLog {
  sequenceName: string;
  timestamp: string;
  status: 'SUCCESS' | 'FAILED' | 'PARTIAL';
  tracks: number;
  clockEvents: SequenceClockEvent[];
}

export interface SequenceClockEvent {
  clockPosition: number;
  crateName: string;
  mode: 'OPTIMIZE' | 'FORCE_NEXT' | 'STOPSET';
  selectedTrack: Track;
  score?: number | null;
  reasoning: string;
  candidatesEvaluated: number;
  rejected?: RejectedTrack[];
}

export interface RejectedTrack {
  trackId: string;
  reason: string;
}

export interface RunSequenceParams {
  export_to_spotify?: boolean;
  save_state?: boolean;
}

// ==================== REORDER ====================

export type ReorderStrategy =
  | 'camelot-chain'
  | 'energy-rise'
  | 'energy-fall'
  | 'dj-flow'
  | 'smooth-tsp'
  | 'full-dj';

export interface ReorderOptions {
  startKey?: string;
  direction?: 'up' | 'down' | 'any';
  secondarySort?: 'energy' | 'tempo' | 'valence';
  profile?: string;
  energyWeight?: number;
  camelotWeight?: number;
  tempoWeight?: number;
}

export interface ReorderRequest {
  strategy: ReorderStrategy;
  options?: ReorderOptions;
  apply?: boolean;
  createNewPlaylist?: boolean;
  newPlaylistName?: string;
}

export interface ReorderResult {
  reorderedTracks: string[];
  qualityScore: number;
  playlistId?: string | null;
  applied: boolean;
  metrics?: ReorderMetrics;
}

export interface ReorderMetrics {
  constraintUsage: 'STRICT' | 'RELAXED' | 'MINIMAL';
  avgTransitionScore: number;
}

export interface CustomReorderParams {
  playlist_id: string;
  apply?: boolean;
}

// ==================== JOBS ====================

export interface JobCreated {
  jobId: string;
  status: 'pending' | 'processing';
  progressUrl: string;
}

export type JobStatus = 'pending' | 'processing' | 'completed' | 'failed';

export interface JobStatusResponse {
  jobId: string;
  status: JobStatus;
  progress: number;
  message?: string;
  result?: unknown;
  error?: JobError;
  createdAt: string;
  updatedAt: string;
}

export interface JobError {
  code: string;
  message: string;
}

// ==================== ERROR ====================

export interface UnauthorizedUserError {
  code: 'NOT_WHITELISTED';
  message: string;
  user: {
    id: string;
    email: string;
    displayName: string;
    spotifyUrl: string;
  };
}

export interface ApiErrorResponse {
  error: UnauthorizedUserError | { code: string; message: string };
}

export interface ApiError {
  error: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
  };
}

// ==================== PAGINATION ====================

export interface PaginationParams {
  limit?: number;
  offset?: number;
}

// ==================== RATE LIMIT ====================

export interface RateLimitInfo {
  limit: number;
  remaining: number;
  reset: number;
}

// ==================== PLAYER ====================

export interface Device {
  deviceId: string | null;
  name: string;
  type: string;
  isActive: boolean;
  volumePercent: number | null;
}

export interface PlaybackStateResponse {
  device: Device | null;
  isPlaying: boolean;
  progressMs: number | null;
  item: Track | null;
}

export interface DevicesListResponse {
  devices: Device[];
}

export interface SelectDeviceRequest {
  deviceId: string;
  forcePlay?: boolean;
}

export interface StartPlaybackRequest {
  contextUri?: string;  // Playlist/album/artist URI (e.g., spotify:playlist:xxxxx)
  uris?: string[];      // Track URIs to play (e.g., ['spotify:track:xxxxx'])
  offset?: { position?: number; uri?: string };  // Start position
  positionMs?: number;  // Position within track in ms
}

export interface SeekRequest {
  positionMs: number;  // Position in milliseconds to seek to
  deviceId?: string;   // Target device ID (if not provided, uses currently active device)
}

export interface SetVolumeRequest {
  volumePercent: number;  // Volume to set (0-100 inclusive)
  deviceId?: string;      // Target device ID (if not provided, uses currently active device)
}

/**
 * Represents the context where a track was played
 * (e.g., from a playlist, album, artist, or liked songs)
 */
export interface PlayContext {
  type: 'playlist' | 'album' | 'artist' | 'collection';
  href: string;
  external_urls: {
    spotify: string;
  };
  uri: string;
}

/**
 * Represents a single play history entry
 * Includes track information, timestamp, and context of where it was played
 */
export interface PlayHistory {
  track: Track;
  playedAt: string; // ISO 8601 timestamp (camelCase from backend)
  context: PlayContext | null;
}

/**
 * API response for recently played tracks
 * Supports pagination via cursor-based navigation
 */
export interface RecentlyPlayedResponse {
  items: PlayHistory[];
  next: string | null;
  cursors: {
    before: string | null;
    after: string | null;
  };
  limit: number;
  href: string;
}
